import {
  Popover,
  type IPopoverProps
} from '@/react/components/core-ui/Popover';
import type { IPopoverPosition } from '@/react/components/core-ui/Popover/IPopoverProps';
import { observer } from 'mobx-react-lite';
import type { FC, ReactNode } from 'react';

import { Button } from '@/react/components/core-ui/Button';
import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import { msg } from '@/services/isomorphic/I18NService';

import S from './styles.base.module.scss';
import { search_box_frame_close } from "@/lang/__generated__/ahnu/search_box_frame_close";

/**
 * The props for the SearchPopover component.
 *
 * We omit some props from IPopoverProps as they will be overridden
 * by the SearchPopover component itself.
 */
export interface ISearchPopoverProps
  extends Omit<IPopoverProps, 'position' | 'direction' | 'className'> {
  /**
   * Include the close button in the popover.
   */
  includeClose?: boolean;

  /**
   * The content to be displayed inside the popover.
   */
  children: ReactNode;
}

export const SearchPopover: FC<ISearchPopoverProps> = observer(
  function SearchPopover({ includeClose = true, children, ...props }) {
    const { onClose } = props;

    // The position where the popover will be anchored
    const position: IPopoverPosition = { x: 'right', y: 'bottom' };
    // The direction in which the popover will be displayed
    const direction: IPopoverPosition = { x: 'left', y: 'bottom' };

    return (
      <Popover
        popoverClassName={S.popover}
        className={S.searchFrame}
        position={position}
        direction={direction}
        {...props}
      >
        {includeClose && (
          <Button
            className={S.closePopover}
            variant="text"
            onClick={onClose}
            ariaLabel={msg(search_box_frame_close)}
          >
            <Icon size={IconSizeProp.Size1X} icon={IconTypes.CloseLight} />
          </Button>
        )}
        <div className={S.container}>{children}</div>
      </Popover>
    );
  }
);

export default SearchPopover;
